import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Search, FileText, Download, Upload, Edit2, Trash2, X } from "lucide-react";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Forms() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState(null); // form doc being renamed

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/forms");
      setForms(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load forms");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return forms;
    return forms.filter((f) => (f.title || "").toLowerCase().includes(s));
  }, [forms, q]);

  const download = async (form) => {
    try {
      const token = localStorage.getItem("karm_token");
      const res = await fetch(`${REACT_APP_BACKEND_URL}/api/forms/${form.id}/file`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = form.file_name || `${form.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Downloading ${form.title}`);
    } catch (e) {
      toast.error(e.message || "Could not download");
    }
  };

  const remove = async (form) => {
    if (!window.confirm(`Delete "${form.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/forms/${form.id}`);
      toast.success("Form deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="p-4 md:p-10 max-w-4xl mx-auto">
      <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="inline-block bg-safety-yellow text-black px-2 py-0.5 font-bold uppercase tracking-widest text-[10px] mb-3">Safety Forms</div>
          <h1 className="font-heading text-4xl md:text-6xl">DOCUMENT LIBRARY</h1>
          <p className="text-muted-foreground mt-2 text-sm md:text-base">
            Company-approved safety forms. <b className="text-foreground">{forms.length} on file</b>.
            {!isOwner && forms.length > 0 && <> Contact your platform owner to add or edit.</>}
          </p>
        </div>
        {isOwner && (
          <button
            data-testid="upload-form-btn"
            onClick={() => setShowUpload(true)}
            className="btn-primary self-start md:self-auto"
          >
            <Upload className="w-5 h-5 mr-2" strokeWidth={3} /> Upload PDF
          </button>
        )}
      </div>

      <div className="brutal-card p-4 md:p-5 mb-5">
        <div className="relative">
          <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="forms-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search forms by title..."
            className="industrial-input pl-11"
          />
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-muted-foreground font-bold uppercase tracking-wider">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="brutal-card p-10 text-center">
          <div className="font-heading text-2xl">
            {forms.length === 0 ? "No forms uploaded yet" : "No forms match your search"}
          </div>
          {forms.length === 0 && isOwner && (
            <div className="text-muted-foreground text-sm mt-2">Tap "Upload PDF" to publish your first safety form.</div>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="forms-list">
          {filtered.map((f) => (
            <div key={f.id} data-testid={`form-${f.id}`} className="brutal-card p-4 flex items-center gap-4">
              <div className="h-12 w-12 bg-safety-yellow text-black border-2 border-black flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-xl leading-tight truncate">{f.title}</div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">
                  {f.file_name || "PDF"}
                  {f.size_bytes ? ` · ${(f.size_bytes / 1024).toFixed(0)} KB` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {isOwner && (
                  <>
                    <button
                      data-testid={`edit-${f.id}`}
                      onClick={() => setEditing(f)}
                      aria-label={`Rename ${f.title}`}
                      className="h-12 w-12 border-2 border-foreground dark:border-safety-yellow hover:bg-safety-yellow hover:text-black inline-flex items-center justify-center"
                    >
                      <Edit2 className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                    <button
                      data-testid={`delete-${f.id}`}
                      onClick={() => remove(f)}
                      aria-label={`Delete ${f.title}`}
                      className="h-12 w-12 border-2 border-status-expired text-status-expired hover:bg-status-expired hover:text-white inline-flex items-center justify-center"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                  </>
                )}
                <button
                  data-testid={`download-${f.id}`}
                  onClick={() => download(f)}
                  aria-label={`Download ${f.title}`}
                  className="h-12 px-4 border-2 border-foreground dark:border-safety-yellow hover:bg-safety-yellow hover:text-black text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2"
                >
                  <Download className="w-4 h-4" strokeWidth={2.5} />
                  <span className="hidden sm:inline">Download</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showUpload && (
        <UploadFormModal
          onClose={() => setShowUpload(false)}
          onDone={() => { setShowUpload(false); load(); }}
        />
      )}
      {editing && (
        <RenameFormModal
          form={editing}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="brutal-card w-full max-w-md bg-background p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <div className="flex items-center justify-between">
          <div className="font-heading text-2xl">{title}</div>
          <button onClick={onClose} aria-label="Close" className="h-10 w-10 border-2 border-foreground dark:border-safety-yellow inline-flex items-center justify-center">
            <X className="w-4 h-4" strokeWidth={3} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function UploadFormModal({ onClose, onDone }) {
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Title required"); return; }
    if (!file) { toast.error("Pick a PDF"); return; }
    if (!/pdf$/i.test(file.name) && file.type !== "application/pdf") {
      toast.error("Only PDF files are supported"); return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("file", file);
      await api.post("/forms", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Form uploaded");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Upload PDF" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4" data-testid="upload-form-modal">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Title</label>
          <input
            data-testid="upload-title"
            required
            className="industrial-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., OSHA 300A Summary"
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">PDF File</label>
          <input
            ref={inputRef}
            data-testid="upload-file"
            type="file"
            accept="application/pdf,.pdf"
            required
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="industrial-input file:mr-3 file:border-0 file:bg-safety-yellow file:text-black file:font-bold file:uppercase file:tracking-wider file:px-3 file:py-2 file:mr-3"
          />
          {file && (
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">
              {file.name} · {(file.size / 1024).toFixed(0)} KB
            </div>
          )}
        </div>
        <button
          type="submit"
          data-testid="upload-submit"
          disabled={busy}
          className="btn-primary w-full disabled:opacity-60"
        >
          <Upload className="w-5 h-5 mr-2" strokeWidth={3} />
          {busy ? "Uploading…" : "Publish Form"}
        </button>
      </form>
    </ModalShell>
  );
}

function RenameFormModal({ form, onClose, onDone }) {
  const [title, setTitle] = useState(form.title || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      await api.patch(`/forms/${form.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Renamed");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Rename failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <ModalShell title="Rename Form" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4" data-testid="rename-form-modal">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Title</label>
          <input
            data-testid="rename-title"
            required
            className="industrial-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <button type="submit" data-testid="rename-submit" disabled={busy} className="btn-primary w-full disabled:opacity-60">
          {busy ? "Saving…" : "Save"}
        </button>
      </form>
    </ModalShell>
  );
}
